import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { CreatePipelineModal } from "../components/CreatePipelineModal";
import { KanbanColumn } from "../components/KanbanColumn";
import { MoveStageModal, MoveStagePayload } from "../components/MoveStageModal";
import { OpportunityCardContent } from "../components/OpportunityCard";
import { OpportunityFormModal } from "../components/OpportunityFormModal";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/format";
import { Opportunity, Pipeline, PipelineStage, Product, UserMini } from "../types/crm";

interface PendingChange {
  opportunity: Opportunity;
  targetStage: PipelineStage;
}

export function PipelineKanbanPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { username } = useAuth();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string>(() => searchParams.get("pipeline") ?? "");
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<UserMini[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const [editing, setEditing] = useState<Opportunity | null>(null);
  const [createPipelineOpen, setCreatePipelineOpen] = useState(false);
  const [productFilter, setProductFilter] = useState<number | "">("");
  const [ownerFilter, setOwnerFilter] = useState<number | "">("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [pipelinesResponse, opportunitiesResponse, productsResponse, usersResponse] =
        await Promise.all([
          api.get<Pipeline[]>("/pipelines/"),
          api.get<Opportunity[]>("/opportunities/"),
          api.get<Product[]>("/products/"),
          api.get<UserMini[]>("/users/"),
        ]);
      setPipelines(pipelinesResponse.data);
      setOpportunities(opportunitiesResponse.data);
      setProducts(productsResponse.data);
      setUsers(usersResponse.data.filter((user) => user.is_active));
      setSelectedSlug((current) => {
        if (current && pipelinesResponse.data.some((p) => p.slug === current)) return current;
        const def = pipelinesResponse.data.find((p) => p.is_default) ?? pipelinesResponse.data[0];
        return def ? def.slug : "";
      });
    } catch {
      setError("Não foi possível carregar o pipeline.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // location.state muda quando o cadastro rápido do header global navega para cá.
  }, [fetchData, location.state]);

  // Carrega as etapas do pipeline selecionado.
  useEffect(() => {
    if (!selectedSlug) return;
    let cancelled = false;
    api
      .get<PipelineStage[]>(`/pipeline-stages/?pipeline=${selectedSlug}`)
      .then((response) => {
        if (!cancelled) setStages(response.data);
      })
      .catch(() => {
        if (!cancelled) setStages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug]);

  // Reflete o pipeline selecionado na URL (?pipeline=slug) para que o botão
  // global "+ Novo" abra o formulário já nesse funil.
  useEffect(() => {
    if (!selectedSlug) return;
    if (searchParams.get("pipeline") === selectedSlug) return;
    const next = new URLSearchParams(searchParams);
    next.set("pipeline", selectedSlug);
    setSearchParams(next, { replace: true });
  }, [selectedSlug, searchParams, setSearchParams]);

  const handleSaved = (opportunity: Opportunity) => {
    setEditing(null);
    setOpportunities((current) => current.map((o) => (o.id === opportunity.id ? opportunity : o)));
    if (opportunity.pipeline_detail) setSelectedSlug(opportunity.pipeline_detail.slug);
  };

  const me = users.find((u) => u.username === username);
  const canModify = (opp: Opportunity) => !!me && (me.is_staff || opp.owner === me.id);

  const handleDelete = async (opp: Opportunity) => {
    if (!window.confirm(`Excluir a oportunidade de ${opp.company_detail.name}? Esta ação é definitiva.`)) return;
    try {
      await api.delete(`/opportunities/${opp.id}/`);
      setOpportunities((current) => current.filter((o) => o.id !== opp.id));
    } catch {
      window.alert("Não foi possível excluir (apenas o responsável ou um admin podem).");
    }
  };

  const handlePipelineCreated = (pipeline: Pipeline) => {
    setCreatePipelineOpen(false);
    setPipelines((current) => [...current, pipeline]);
    setSelectedSlug(pipeline.slug);
  };

  const applyStageChange = async (
    opportunity: Opportunity,
    targetStage: PipelineStage,
    extra?: Partial<MoveStagePayload>
  ) => {
    const previous = opportunities;
    setOpportunities((current) =>
      current.map((opp) =>
        opp.id === opportunity.id ? { ...opp, stage: targetStage.id, stage_detail: targetStage } : opp
      )
    );

    try {
      const response = await api.patch<Opportunity>(`/opportunities/${opportunity.id}/`, {
        stage: targetStage.id,
        ...extra,
      });
      setOpportunities((current) =>
        current.map((opp) => (opp.id === opportunity.id ? response.data : opp))
      );
    } catch {
      setOpportunities(previous);
      setMoveError("Não foi possível mover a oportunidade.");
      throw new Error("stage change failed");
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(Number(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const opportunity = opportunities.find((opp) => opp.id === Number(active.id));
    const targetStage = stages.find((stage) => stage.id === Number(over.id));
    if (!opportunity || !targetStage || opportunity.stage === targetStage.id) return;

    setMoveError(null);
    setPendingChange({ opportunity, targetStage });
  };

  const activeOpportunity = opportunities.find((opp) => opp.id === activeId);

  // Oportunidades do pipeline selecionado (base para subtítulo e colunas).
  const pipelineOpportunities = opportunities.filter(
    (opp) => opp.pipeline_detail?.slug === selectedSlug
  );
  const openOpportunities = pipelineOpportunities.filter((opp) => opp.status === "aberta");
  const openMrr = openOpportunities.reduce((sum, opp) => sum + Number(opp.mrr_estimated), 0);

  const filteredOpportunities = pipelineOpportunities.filter((opp) => {
    if (productFilter !== "" && !opp.products.includes(productFilter)) return false;
    if (ownerFilter !== "" && opp.owner !== ownerFilter) return false;
    return true;
  });
  const hasFilters = productFilter !== "" || ownerFilter !== "";

  const subtitle =
    pipelineOpportunities.length === 0
      ? `${stages.length} fases · nenhuma oportunidade ainda`
      : `${openOpportunities.length} oportunidades abertas${
          openMrr > 0 ? ` · ${formatCurrency(openMrr)} MRR` : ""
        }`;

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Pipeline</h1>
          <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Selecionar pipeline"
            value={selectedSlug}
            onChange={(event) => setSelectedSlug(event.target.value)}
            className="h-9 min-w-[190px] rounded-[10px] border border-border-subtle bg-white px-3 text-sm font-medium text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {pipelines.map((pipeline) => (
              <option key={pipeline.slug} value={pipeline.slug}>
                {pipeline.name}
                {pipeline.is_default ? " (padrão)" : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setCreatePipelineOpen(true)}
            className="h-9 rounded-[10px] border border-border-subtle px-3 text-sm font-medium text-slate-600 hover:bg-surface"
          >
            + Criar novo pipeline
          </button>
        </div>
      </div>

      {!isLoading && !error && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            aria-label="Filtrar por produto"
            value={productFilter}
            onChange={(event) =>
              setProductFilter(event.target.value ? Number(event.target.value) : "")
            }
            className="rounded-[10px] border border-border-subtle bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Todos os produtos</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>

          <select
            aria-label="Filtrar por responsável"
            value={ownerFilter}
            onChange={(event) =>
              setOwnerFilter(event.target.value ? Number(event.target.value) : "")
            }
            className="rounded-[10px] border border-border-subtle bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Todos os responsáveis</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </select>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setProductFilter("");
                setOwnerFilter("");
              }}
              className="text-sm font-medium text-primary hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {isLoading && <p className="mt-6 text-sm text-slate-500">Carregando...</p>}

      {!isLoading && error && (
        <div className="mt-6 rounded-[10px] border border-border-subtle bg-surface p-4">
          <p className="text-sm text-red-600">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="mt-3 rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {moveError && <p className="mt-4 text-sm text-red-600">{moveError}</p>}

          <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="mt-6 flex gap-4 overflow-x-auto">
              {stages.map((stage) => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  opportunities={filteredOpportunities.filter((opp) => opp.stage === stage.id)}
                  onEdit={setEditing}
                  onDelete={handleDelete}
                  canModify={canModify}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeOpportunity && (
                <div className="w-72 rounded-[10px] border border-border-subtle bg-surface p-3 shadow-md">
                  <OpportunityCardContent opportunity={activeOpportunity} />
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </>
      )}

      {pendingChange && (
        <MoveStageModal
          opportunity={pendingChange.opportunity}
          targetStage={pendingChange.targetStage}
          onCancel={() => setPendingChange(null)}
          onConfirm={async (payload) => {
            await applyStageChange(pendingChange.opportunity, pendingChange.targetStage, payload);
            setPendingChange(null);
          }}
        />
      )}

      {editing && (
        <OpportunityFormModal
          opportunity={editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}

      {createPipelineOpen && (
        <CreatePipelineModal
          onClose={() => setCreatePipelineOpen(false)}
          onCreated={handlePipelineCreated}
        />
      )}
    </div>
  );
}
